/**
 * Where the pose assets live, and the rule that keeps them ours.
 *
 * Every constant the engine reads is here rather than in engine code, so the model variant, the
 * WASM location and the detection thresholds are all changed in one config-shaped place.
 *
 * The model `.task` bundle and the tasks-vision WASM runtime are both served from this app's own
 * origin. MediaPipe's published sample loads both from a CDN; doing that would leak the fact and
 * timing of a child's camera session to a third party and would make the build non-deterministic.
 * Architecture §23/§25 #7 forbid it, so {@link assertSameOriginAssetPath} makes an external URL a
 * hard failure rather than a review finding.
 */

import { POSE_MODEL_PATH, POSE_WASM_BASE_PATH } from './pose-asset-paths';
import type { PoseEngineAssets, PoseEngineOptions } from './pose-types';

// The two paths themselves live in `pose-asset-paths.ts`, which imports nothing, so the end-to-end
// console allowlist can read the canonical model URL without the engine's DOM-typed surface coming
// with it (S6-8). Re-exported here, so every existing reader is unchanged.
export { POSE_MODEL_PATH, POSE_WASM_BASE_PATH } from './pose-asset-paths';

export const POSE_ENGINE_ASSETS: PoseEngineAssets = {
  wasmBasePath: POSE_WASM_BASE_PATH,
  modelAssetPath: POSE_MODEL_PATH,
};

/**
 * Detection defaults.
 *
 * `numPoses: 1` is the Phase 3 single-player case; Phase 4 raises it to 2 (CAM-07). The three
 * confidence floors are MediaPipe's own defaults, kept explicit so a future change to them is a
 * visible edit. They are *not* the D-059 PS3-1 adapter gate — that threshold belongs to the
 * detector layer built in Slice B and is deliberately absent here.
 */
export const POSE_ENGINE_DEFAULTS: Omit<PoseEngineOptions, 'assets' | 'runningMode'> = {
  delegate: 'GPU',
  numPoses: 1,
  minPoseDetectionConfidence: 0.5,
  minPosePresenceConfidence: 0.5,
  minTrackingConfidence: 0.5,
};

/**
 * Rejects any asset path that could reach off this origin.
 *
 * Only root-relative paths are allowed. A scheme (`https:`, `data:`, `blob:`) or a
 * protocol-relative `//host` prefix is refused, and so is a bare relative path, because that
 * resolves against whatever route happens to be current and is therefore not a stable claim
 * about the origin.
 */
export function assertSameOriginAssetPath(path: string, label: string): string {
  if (path.startsWith('//')) {
    throw new Error(`${label} must be same-origin; "${path}" is protocol-relative.`);
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) {
    throw new Error(`${label} must be same-origin; "${path}" carries a URL scheme.`);
  }
  if (!path.startsWith('/')) {
    throw new Error(`${label} must be a root-relative path; "${path}" is not.`);
  }
  return path;
}

/** Validates a whole asset pair, returning it unchanged so it can be used inline. */
export function assertSameOriginAssets(assets: PoseEngineAssets): PoseEngineAssets {
  assertSameOriginAssetPath(assets.wasmBasePath, 'Pose WASM base path');
  assertSameOriginAssetPath(assets.modelAssetPath, 'Pose model path');
  return assets;
}
